import React, { useState } from 'react';
import { 
    Typography, 
    Box, 
    Button, 
    TextField,
    Alert
} from '@mui/material';
import LoadingSpinner from '../common/LoadingSpinner';

const RECAPTCHA_SITE_KEY = '6LcZAkwrAAAAAA7gZjMP_OT6IGBEwPYmaSQsg7a4';

const OpenHouseRegistrationForm = ({ 
    selectedEvent, 
    onCancel, 
    onSuccess 
}) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: ''
    });
    const [errors, setErrors] = useState({
        name: '',
        email: '',
        phone: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({
        success: false,
        message: ''
    });

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                return value.trim() === '' ? 'Name is required' : '';
            case 'email':
                if (value.trim() === '') return 'Email is required';
                return !validateEmail(value) ? 'Invalid email format' : '';
            case 'phone':
                if (value.trim() === '') return '';
                const phoneRegex = /^[\s\-.()*]*(\d[\s\-.()]*){10}[\s\-.()]*$/;
                return !phoneRegex.test(value) ? 'Invalid phone number format' : '';
            default:
                return '';
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    };

    const loadRecaptcha = async () => {
        try {
            if (!window.grecaptcha) {
                const script = document.createElement('script');
                script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
                script.async = true;
                script.defer = true;
                
                await new Promise((resolve, reject) => {
                    script.onload = () => {
                        window.grecaptcha.ready(() => {
                            resolve();
                        });
                    };
                    
                    script.onerror = (error) => {
                        console.error('Script load error:', error);
                        reject(new Error('Failed to load reCAPTCHA script'));
                    };
                    
                    document.body.appendChild(script);
                });
            } else {
                await new Promise((resolve, reject) => {
                    if (typeof window.grecaptcha.ready === 'function') {
                        window.grecaptcha.ready(() => {
                            resolve();
                        });
                    } else {
                        console.error('grecaptcha.ready is not a function');
                        reject(new Error('reCAPTCHA not properly initialized'));
                    }
                });
            }
        } catch (error) {
            console.error('Failed to load reCAPTCHA:', error);
            throw error;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const newErrors = {
            name: validateField('name', formData.name),
            email: validateField('email', formData.email),
            phone: validateField('phone', formData.phone)
        };

        setErrors(newErrors);

        const hasErrors = Object.values(newErrors).some(error => error !== '');

        if (hasErrors) {
            return;
        }

        setIsSubmitting(true);

        try {
            await loadRecaptcha();
            const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {action: 'submit'});
            
            const response = await fetch(`${process.env.PUBLIC_URL}/send-email.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formData,
                    formType: 'openhouse',
                    eventDate: selectedEvent.date,
                    eventTime: selectedEvent.time,
                    captchaToken: token
                })
            });

            const result = await response.json();
            
            if (result.success) {
                setSubmitStatus({
                    success: true,
                    message: 'Thank you for registering! We will send you more details about the open house session.'
                });
                setFormData({ name: '', email: '', phone: '' });
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                throw new Error(result.error || 'Failed to submit registration');
            }
        } catch (error) {
            console.error('Error in form submission:', error);
            setSubmitStatus({
                success: false,
                message: error.message || 'Sorry, there was an error processing your registration. Please try again later.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" component="div" sx={{ 
                    fontWeight: 'bold', 
                    mb: 1,
                    pl: 0, // Left padding only
                    mt: 1  // Add small margin-top for extra spacing
                }}>
                    Register for Open House
                </Typography>
                {selectedEvent && (
                    <Typography variant="body2" color="text.secondary">
                        {selectedEvent.date} at {selectedEvent.time}
                    </Typography>
                )}
            </Box>

            <form onSubmit={handleSubmit}>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    Please provide your contact information so we can send you more details about this open house session.
                </Typography>

                {submitStatus.success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {submitStatus.message}
                    </Alert>
                )}
                
                {!submitStatus.success && submitStatus.message && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {submitStatus.message}
                    </Alert>
                )}

                <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    error={!!errors.name}
                    helperText={errors.name}
                    disabled={isSubmitting}
                    sx={{ mb: 2 }}
                />

                <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    error={!!errors.email}
                    helperText={errors.email}
                    disabled={isSubmitting}
                    sx={{ mb: 2 }}
                />

                <TextField
                    fullWidth
                    label="Phone Number (Optional)"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    disabled={isSubmitting}
                    sx={{ mb: 3 }}
                />
                
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button 
                        onClick={onCancel} 
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="submit"
                        variant="contained" 
                        color="secondary"
                        disabled={isSubmitting}
                        sx={{ minWidth: 120 }}
                    >
                        {isSubmitting ? <LoadingSpinner size={20} /> : 'Register'}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default OpenHouseRegistrationForm;